import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { waAnchorProps } from "../../lib/whatsapp";
import axios from "axios";
import * as pushLib from "../../lib/pushClient";
import { Bell, BellOff, Check, X, ExternalLink, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function authHeaders() {
  const t = localStorage.getItem("admin_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
}

// Tiny "ding" via WebAudio (no asset file required)
function playDing() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = "sine";
    o.frequency.setValueAtTime(880, ctx.currentTime);
    o.frequency.exponentialRampToValueAtTime(660, ctx.currentTime + 0.15);
    g.gain.setValueAtTime(0.001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.02);
    g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
    o.connect(g);
    g.connect(ctx.destination);
    o.start();
    o.stop(ctx.currentTime + 0.45);
  } catch (_) {}
}

export default function NotificationsBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushSupported, setPushSupported] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [prefs, setPrefs] = useState(null);
  const lastIdRef = useRef(null);
  const dropdownRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/notifications?limit=30`, { headers: authHeaders() });
      const newest = r.data.items?.[0]?.id;
      // Play sound only when a NEW notification arrived after first load
      if (lastIdRef.current && newest && newest !== lastIdRef.current) {
        playDing();
      }
      lastIdRef.current = newest || lastIdRef.current;
      setItems(r.data.items || []);
      setUnread(r.data.unread || 0);
    } catch (e) {
      // silent — probably token expired, admin will re-login
    }
  }, []);

  // Initial + poll every 20s
  useEffect(() => {
    let cancelled = false;
    load();
    const t = setInterval(() => {
      if (!cancelled) load();
    }, 20000);
    return () => { cancelled = true; clearInterval(t); };
  }, [load]);

  // Check push subscription state (shared helper in lib/pushClient)
  const refreshPushState = useCallback(async () => {
    if (!pushLib.pushSupported()) { setPushSupported(false); return; }
    setPushSupported(true);
    // Lo stato "attive" dipende SOLO dall'iscrizione presente nel browser: un errore di rete/server
    // (es. durante un Deploy o con sessione scaduta) non deve farle sembrare disattivate.
    let sub = null;
    try { sub = await pushLib.getPushSubscription(); } catch (_) { sub = null; }
    setPushEnabled(!!sub);
    if (!sub) { setPrefs(null); return; }
    try {
      const p = await pushLib.fetchSubscriptionPrefs(sub);
      setPrefs(p);
      if (!p.found) await pushLib.savePushSubscription(sub, [], pushLib.deviceLabel());
    } catch (_) { /* server momentaneamente non raggiungibile: riproviamo al prossimo giro */ }
  }, []);

  useEffect(() => {
    refreshPushState();
    const t = setInterval(refreshPushState, 60000);
    return () => clearInterval(t);
  }, [refreshPushState]);
  useEffect(() => {
    const onChange = () => refreshPushState();
    window.addEventListener("pozzo-push-changed", onChange);
    return () => window.removeEventListener("pozzo-push-changed", onChange);
  }, [refreshPushState]);

  // Close dropdown on outside click
  useEffect(() => {
    function onDoc(e) {
      if (!dropdownRef.current) return;
      if (!dropdownRef.current.contains(e.target)) setOpen(false);
    }
    if (open) document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const enablePush = async () => {
    setError(""); setBusy(true);
    try { await pushLib.enablePush([]); await refreshPushState(); window.dispatchEvent(new Event("pozzo-push-changed")); }
    catch (e) { setError(e?.response?.data?.detail || e.message || String(e)); }
    finally { setBusy(false); }
  };

  const disablePush = async () => {
    setBusy(true);
    try { await pushLib.disablePush(); setPushEnabled(false); setPrefs(null); window.dispatchEvent(new Event("pozzo-push-changed")); }
    catch (_) {} finally { setBusy(false); }
  };

  const toggleKind = async (key) => {
    const sub = await pushLib.getPushSubscription();
    if (!sub || !prefs) return;
    const all = prefs.groups.map((g) => g.key);
    const cur = prefs.kinds.length ? prefs.kinds : all;
    const next = cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key];
    const kinds = next.length === all.length ? [] : next;
    setPrefs({ ...prefs, kinds });
    await pushLib.savePushSubscription(sub, kinds, pushLib.deviceLabel());
  };

  const [testResult, setTestResult] = useState(null);
  const testPush = async () => {
    setBusy(true); setTestResult(null);
    try {
      const r = await pushLib.sendTestPush();
      setTestResult(r?.data || null);
      setTimeout(load, 800);
    } catch (e) { setTestResult({ error: e?.response?.data?.detail || e.message }); }
    finally {
      setBusy(false);
    }
  };

  const markRead = async (id) => {
    await axios.post(`${API}/admin/notifications/${id}/read`, {}, { headers: authHeaders() });
    load();
  };

  const markAllRead = async () => {
    await axios.post(`${API}/admin/notifications/read-all`, {}, { headers: authHeaders() });
    load();
  };

  const iconFor = useMemo(() => (k) => {
    if (k === "massage_booking") return "🧖";
    if (k === "promemoria") return "🔔";
    if (k === "spa_booking") return "💆";
    if (k === "takeaway_order") return "🛍️";
    if (k === "shop_order") return "🛒";
    if (k === "restaurant_booking") return "🍽️";
    if (k === "gift") return "🎁";
    if (k === "horse_booking") return "🐴";
    if (k === "event_booking") return "✨";
    if (k === "test") return "🔔";
    return "🔔";
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative inline-flex items-center justify-center rounded-full border border-[#f7ebdf]/30 h-9 w-9 hover:bg-[#f7ebdf]/10"
        aria-label="Notifiche"
        data-testid="notifications-bell"
      >
        <Bell className="h-4 w-4" />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center px-1"
                data-testid="notifications-badge">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="fixed left-2 right-2 top-14 sm:absolute sm:left-auto sm:right-0 sm:top-full sm:mt-2 sm:w-96 max-h-[80vh] rounded-xl bg-white text-[#2b1410] border border-[#e8dcc8] shadow-xl overflow-hidden z-[10000] flex flex-col"
             data-testid="notifications-dropdown">
          <div className="p-3 border-b border-[#e8dcc8] flex items-center gap-2">
            <div className="font-serif font-bold text-sm flex-1">Notifiche</div>
            {unread > 0 && (
              <button onClick={markAllRead} className="text-xs text-[#4a1c17] hover:underline"
                      data-testid="notifications-mark-all-read">
                Segna tutte lette
              </button>
            )}
          </div>

          {/* Push toggle */}
          <div className={`p-3 border-b border-[#e8dcc8] ${pushEnabled ? "bg-emerald-50" : "bg-amber-50"}`}>
            {!pushSupported ? (
              <p className="text-xs text-[#7c5a4e]">
                Il tuo browser non supporta le notifiche push. Le notifiche in-app funzionano comunque.
              </p>
            ) : pushEnabled ? (
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-full bg-emerald-500 text-white flex items-center justify-center">
                  <Check className="h-4 w-4" />
                </div>
                <div className="flex-1 text-xs">
                  <div className="font-semibold text-emerald-900">Notifiche attive su questo dispositivo</div>
                  <div className="text-emerald-800">Riceverai un alert anche a schermo bloccato</div>
                </div>
                <button onClick={testPush} disabled={busy}
                        className="rounded-lg border border-emerald-300 bg-white px-2 py-1 text-xs text-emerald-800 hover:bg-emerald-100 disabled:opacity-50"
                        data-testid="notifications-test">
                  Test
                </button>
                <button onClick={disablePush} disabled={busy}
                        className="rounded-lg border border-emerald-300 bg-white px-2 py-1 text-xs text-emerald-800 hover:bg-emerald-100 disabled:opacity-50"
                        title="Disattiva">
                  <BellOff className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : null}
            {testResult && (
              <div className="mx-3 mb-2 rounded-lg border border-[#e8dcc8] bg-[#faf3ee] p-2 text-[11px] text-[#5a3a30]" data-testid="notifications-test-result">
                {testResult.error ? <span className="text-red-700">{testResult.error}</span> : !testResult.configured ? <span className="text-red-700">Server non configurato per le notifiche push (chiavi VAPID mancanti).</span> : (
                  <>
                    <div className="font-semibold mb-1">Invio di prova → {testResult.devices} dispositivo/i</div>
                    {(testResult.push || []).map((d, i) => (
                      <div key={i} className="flex justify-between gap-2">
                        <span>{d.label}{d.created_at ? ` · attivato il ${new Date(d.created_at).toLocaleDateString("it-IT")}` : ""}</span>
                        <span className={d.status === "sent" ? "text-emerald-700 font-semibold" : "text-red-700 font-semibold"}>{d.status === "sent" ? "inviata" : d.status === "dead" ? "scaduta (rimossa)" : d.status === "skipped" ? "esclusa dalle preferenze" : "fallita"}</span>
                      </div>
                    ))}
                    {testResult.devices === 0 && <div>Nessun dispositivo registrato: attiva le notifiche su questo dispositivo.</div>}
                  </>
                )}
              </div>
            )}
            {pushSupported && pushEnabled && prefs?.groups && (
              <div className="mt-2 flex flex-wrap gap-1" data-testid="push-kind-prefs">
                {prefs.groups.map((g) => { const on = !prefs.kinds.length || prefs.kinds.includes(g.key); return (
                  <button key={g.key} type="button" onClick={() => toggleKind(g.key)} data-testid={`push-kind-${g.key}`} data-on={on}
                    className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${on ? "bg-emerald-600 border-emerald-600 text-white" : "bg-white border-emerald-300 text-emerald-800 line-through opacity-70"}`}>{g.label}</button>); })}
              </div>
            )}
            {pushSupported && !pushEnabled ? (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <div className="h-8 w-8 rounded-full bg-amber-500 text-white flex items-center justify-center">
                    <Bell className="h-4 w-4" />
                  </div>
                  <div className="flex-1 text-xs">
                    <div className="font-semibold text-amber-900">Attiva notifiche push</div>
                    <div className="text-amber-800">Ricevi alert sul telefono per ogni prenotazione</div>
                  </div>
                </div>
                <button
                  onClick={enablePush}
                  disabled={busy}
                  className="w-full inline-flex items-center justify-center gap-1.5 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 text-white px-3 py-1.5 text-xs font-semibold disabled:opacity-60"
                  data-testid="notifications-enable-push"
                >
                  {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Bell className="h-3.5 w-3.5" />}
                  Attiva su questo dispositivo
                </button>
                {error && (
                  <div className="mt-2 text-[11px] text-red-700 bg-red-50 border border-red-200 rounded p-1.5">
                    {error}
                  </div>
                )}
              </div>
            ) : null}
          </div>

          {/* List */}
          <div className="overflow-y-auto flex-1">
            {items.length === 0 ? (
              <div className="p-8 text-center text-sm text-[#7c5a4e]">
                Nessuna notifica ancora.
              </div>
            ) : items.map((n) => (
              <div key={n.id}
                   className={`p-3 border-b border-[#e8dcc8] flex gap-2 ${n.read ? "" : "bg-amber-50/50"}`}
                   data-testid={`notification-item-${n.id}`}>
                <div className="text-xl leading-none">{iconFor(n.kind)}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm text-[#2b1410] truncate">{n.title}</div>
                  <div className="text-xs text-[#5a3a30] break-words">{n.body}</div>
                  <div className="mt-1 flex items-center gap-2 text-[10px] text-[#7c5a4e]">
                    <span>{new Date(n.created_at).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                    {n.data?.phone && (
                      <a {...waAnchorProps(n.data.phone)}
                         className="text-emerald-700 hover:underline inline-flex items-center gap-0.5">
                        WA <ExternalLink className="h-2.5 w-2.5" />
                      </a>
                    )}
                    {!n.read && (
                      <button onClick={() => markRead(n.id)} className="ml-auto text-[#4a1c17] hover:underline">
                        Segna letta
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
